import { GetIssueResponseDtoStatusEnum } from '@issuemanager/api-client';
import {
  ActionStatusChip,
  ActionStatusData,
  ExtendedAutocomplete,
  IssueStatusChip,
  IssueStatusData,
  sortOptionsBySelected,
  useActionStatuses,
  useIssueStatuses,
} from '@issuemanager/apps/client/components';

import { ActionStatus } from '@issuemanager/constants';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExtendedAutocompleteProps } from '../../extended-autocomplete/types';
import type { MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export type GroupedStatus = {
  group: string;
} & (IssueStatusData | ActionStatusData);

export type StatusMultipleSelectorProps = MultipleSelectorProps<
  GroupedStatus[],
  GroupedStatus
>;

export function StatusMultipleSelector(
  props: StatusMultipleSelectorProps,
): React.JSX.Element {
  const { value, onChange, enabled: _enabled, ...rest } = props;

  const [partialValue, setPartialValue] = useState(value ?? []);
  const issueStatuses = useIssueStatuses();
  const actionStatuses = useActionStatuses();
  const { t } = useTranslation();

  const issueStatusLabel = t('statusLabels.issue');
  const actionStatusLabel = t('statusLabels.action');

  const issueStatusesGrouped = issueStatuses.map((c) => ({
    ...c,
    group: issueStatusLabel,
  }));

  const actionStatusGrouped = actionStatuses.map((c) => ({
    ...c,
    group: actionStatusLabel,
  }));

  const options = mergePaginatedDataAndValue(
    [[...issueStatusesGrouped], [...actionStatusGrouped]],
    value ?? [],
    (option) => `${option.id}-${option.group}`,
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
    isOptionEqualToValue: (option, valueToCompare) =>
      option.id === valueToCompare.id && option.group === valueToCompare.group,
  });

  const sortedOptions = sortOptionsBySelected(
    options,
    partialValue,
    autocompleteProps.isOptionEqualToValue,
  );

  const finalOptions = sortedOptions
    .filter((o) => o.group === issueStatusLabel)
    .concat(sortedOptions.filter((o) => o.group === actionStatusLabel));

  const renderOption: NonNullable<
    ExtendedAutocompleteProps<
      IssueStatusData | ActionStatusData,
      true, // multiple?
      true, // disableClearable?
      false // freeSolo?
    >['renderOption']
  > = (renderOptionProps, option) => (
    <li {...renderOptionProps}>
      {option.group === issueStatusLabel ? (
        <IssueStatusChip
          status={option.value as GetIssueResponseDtoStatusEnum}
        />
      ) : (
        <ActionStatusChip status={option.value as ActionStatus} />
      )}
    </li>
  );

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={finalOptions}
      getOptionKey={(option) => `${option.id}-${option.group}`}
      getOptionLabel={(option) => `${option.id} <${option.label}>`}
      groupBy={(option) => option.group}
      renderOption={renderOption}
      {...rest}
    />
  );
}
